import React, { useEffect, useState, useCallback } from "react";
import "./Blog.css";
import api from "../api";

function Blog() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentPostImages, setCurrentPostImages] = useState([]);
  const [modalPost, setModalPost] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);

  const fetchBlogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: currentPage };
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      const res = await api.get("blog/", { params });
      if (currentPage === 1) {
        setPosts(res.data.results);
      } else {
        setPosts(prev => [...prev, ...res.data.results]);
      }
      setHasMore(!!res.data.next);
    } catch (err) {
      console.error("Error fetching blogs:", err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedCategory]);

  useEffect(() => {
    fetchCategories();
    fetchBlogs();
  }, [fetchBlogs]);

  const fetchCategories = async () => {
    try {
      const res = await api.get("blog/categories/");
      setCategories(res.data);
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const handleCategoryChange = (categorySlug) => {
    setSelectedCategory(categorySlug);
    setCurrentPage(1);
    setPosts([]);
  };

  const loadMore = () => {
    setCurrentPage(prev => prev + 1);
  };

  const filteredPosts = posts.filter(
    (post) =>
      post.title?.toLowerCase().includes(search.toLowerCase()) ||
      post.description?.toLowerCase().includes(search.toLowerCase())
  );

  const resolveMediaUrl = (url) => {
    if (!url) return "";
    if (url.startsWith("http")) return url;
    const base = process.env.REACT_APP_API_URL.replace("/api/", "");
    return `${base}${url}`;
  };

  const openModal = (post) => {
    setModalPost(post);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setModalPost(null);
    document.body.style.overflow = 'unset';
  };

  const openLightbox = (images, index) => {
    setCurrentPostImages(images);
    setCurrentImageIndex(index);
    setLightboxImage(images[index]);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    setCurrentPostImages([]);
    setCurrentImageIndex(0);
  };

  const sharePost = (post, platform) => {
    const url = window.location.href;
    const text = `${post.title} - ${post.description.substring(0, 100)}...`;

    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`,
      email: `mailto:?subject=${encodeURIComponent(post.title)}&body=${encodeURIComponent(text + '\n\n' + url)}`
    };

    if (platform === 'copy') {
      navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
    } else {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxImage || !currentPostImages.length) return;

      if (e.key === "ArrowRight") {
        e.stopPropagation();
        const newIndex = (currentImageIndex + 1) % currentPostImages.length;
        setCurrentImageIndex(newIndex);
        setLightboxImage(currentPostImages[newIndex]);
      }
      if (e.key === "ArrowLeft") {
        e.stopPropagation();
        const newIndex = (currentImageIndex - 1 + currentPostImages.length) % currentPostImages.length;
        setCurrentImageIndex(newIndex);
        setLightboxImage(currentPostImages[newIndex]);
      }
      if (e.key === "Escape") {
        closeLightbox();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxImage, currentImageIndex, currentPostImages]);

  if (loading && currentPage === 1) {
    return (
      <div className="blog-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading blog posts…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="blog-container">
      <header className="blog-header">
        <h1 className="blog-title">Community Blog</h1>
        <p className="blog-subtitle">
          Explore stories, updates, and experiences shared by the Marakwet Community in Australia.
        </p>
      </header>

      <div className="blog-controls">
        <div className="search-wrapper">
          <input
            type="text"
            placeholder="🔍 Search posts by title or description..."
            className="search-bar"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="category-filter">
          <button
            className={`category-btn ${selectedCategory === 'all' ? 'active' : ''}`}
            onClick={() => handleCategoryChange('all')}
          >
            All Posts
          </button>
          {categories.map(category => (
            <button
              key={category.id}
              className={`category-btn ${selectedCategory === category.slug ? 'active' : ''}`}
              onClick={() => handleCategoryChange(category.slug)}
            >
              {category.name}
            </button>
          ))}
        </div>
      </div>

      {filteredPosts.length === 0 ? (
        <div className="no-posts">
          <svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <circle cx="11" cy="11" r="8" strokeWidth="2"/>
            <path d="m21 21-4.35-4.35" strokeWidth="2"/>
          </svg>
          <p>No posts found.</p>
        </div>
      ) : (
        <>
          <div className="posts-grid">
            {filteredPosts.map((post) => (
              <article className="post-card" key={post.id}>
                {/* Image Gallery */}
                {post.images && post.images.length > 0 && (
                  <div className="post-gallery">
                    <div className={`gallery-grid ${post.images.length === 1 ? 'single' : post.images.length === 2 ? 'double' : 'multiple'}`}>
                      {post.images.slice(0, 4).map((img, index) => (
                        <div
                          key={img.id}
                          className="gallery-item"
                          onClick={() => openLightbox(post.images, index)}
                        >
                          <img
                            src={resolveMediaUrl(img.image)}
                            alt={img.caption || post.title}
                            loading="lazy"
                          />
                          {index === 3 && post.images.length > 4 && (
                            <div className="more-images-overlay">
                              <span>+{post.images.length - 4}</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {post.images.length > 1 && (
                      <div className="gallery-indicator">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                          <rect x="3" y="3" width="7" height="7" rx="1"/>
                          <rect x="14" y="3" width="7" height="7" rx="1"/>
                          <rect x="3" y="14" width="7" height="7" rx="1"/>
                          <rect x="14" y="14" width="7" height="7" rx="1"/>
                        </svg>
                        <span>{post.images.length} photos</span>
                      </div>
                    )}
                  </div>
                )}

                <div className="post-content">
                  {post.category && (
                    <span className="post-category">{post.category.name}</span>
                  )}

                  <h2 className="post-title">{post.title}</h2>

                  {post.description && (
                    <div className="post-description-wrapper">
                      <p className="post-description">
                        {post.description.length > 200
                          ? `${post.description.substring(0, 200)}...`
                          : post.description
                        }
                      </p>
                      {post.description.length > 200 && (
                        <button
                          className="read-more-btn"
                          onClick={() => openModal(post)}
                        >
                          Read More
                        </button>
                      )}
                    </div>
                  )}

                  <div className="post-footer">
                    <div className="post-meta">
                      <time>{new Date(post.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}</time>
                    </div>

                    <div className="post-actions">
                      <div className="share-dropdown">
                        <button className="share-btn">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <circle cx="18" cy="5" r="3" strokeWidth="2"/>
                            <circle cx="6" cy="12" r="3" strokeWidth="2"/>
                            <circle cx="18" cy="19" r="3" strokeWidth="2"/>
                            <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" strokeWidth="2"/>
                            <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" strokeWidth="2"/>
                          </svg>
                          Share
                        </button>
                        <div className="share-menu">
                          <button onClick={() => sharePost(post, 'facebook')}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                            </svg>
                            Facebook
                          </button>
                          <button onClick={() => sharePost(post, 'twitter')}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                            </svg>
                            Twitter
                          </button>
                          <button onClick={() => sharePost(post, 'linkedin')}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                            </svg>
                            LinkedIn
                          </button>
                          <button onClick={() => sharePost(post, 'whatsapp')}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                            </svg>
                            WhatsApp
                          </button>
                          <button onClick={() => sharePost(post, 'email')}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" strokeWidth="2"/>
                              <polyline points="22,6 12,13 2,6" strokeWidth="2"/>
                            </svg>
                            Email
                          </button>
                          <button onClick={() => sharePost(post, 'copy')}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2" strokeWidth="2"/>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" strokeWidth="2"/>
                            </svg>
                            Copy Link
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {hasMore && (
            <div className="load-more-wrapper">
              <button
                className="load-more-btn"
                onClick={loadMore}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="small-spinner"></div>
                    Loading...
                  </>
                ) : (
                  'Load More Posts'
                )}
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal for Read More */}
      {modalPost && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeModal}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <line x1="18" y1="6" x2="6" y2="18" strokeWidth="2"/>
                <line x1="6" y1="6" x2="18" y2="18" strokeWidth="2"/>
              </svg>
            </button>

            {modalPost.category && (
              <span className="modal-category">{modalPost.category.name}</span>
            )}

            <h2 className="modal-title">{modalPost.title}</h2>

            {modalPost.images && modalPost.images.length > 0 && (
              <div className="modal-gallery">
                {modalPost.images.map((img, index) => (
                  <img
                    key={img.id}
                    src={resolveMediaUrl(img.image)}
                    alt={img.caption || modalPost.title}
                    onClick={() => openLightbox(modalPost.images, index)}
                    className="modal-image"
                  />
                ))}
              </div>
            )}

            <p className="modal-description">{modalPost.description}</p>

            {modalPost.video && (
              <video
                controls
                className="modal-video"
                src={resolveMediaUrl(modalPost.video)}
              >
                Your browser does not support the video tag.
              </video>
            )}

            {modalPost.link && modalPost.link.trim() !== "" && (
              <a
                href={modalPost.link.startsWith("http") ? modalPost.link : `https://${modalPost.link}`}
                target="_blank"
                rel="noopener noreferrer"
                className="modal-link"
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" strokeWidth="2"/>
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" strokeWidth="2"/>
                </svg>
                View related content
              </a>
            )}

            <div className="modal-footer">
              <time className="modal-date">
                {new Date(modalPost.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
              </time>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxImage && (
        <div className="lightbox" onClick={closeLightbox}>
          <button className="lightbox-close" onClick={closeLightbox}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <line x1="18" y1="6" x2="6" y2="18" strokeWidth="2"/>
              <line x1="6" y1="6" x2="18" y2="18" strokeWidth="2"/>
            </svg>
          </button>

          {currentPostImages.length > 1 && (
            <>
              <button className="lightbox-prev" onClick={(e) => {
                e.stopPropagation();
                const newIndex = (currentImageIndex - 1 + currentPostImages.length) % currentPostImages.length;
                setCurrentImageIndex(newIndex);
                setLightboxImage(currentPostImages[newIndex]);
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <polyline points="15 18 9 12 15 6" strokeWidth="2"/>
                </svg>
              </button>
              <button className="lightbox-next" onClick={(e) => {
                e.stopPropagation();
                const newIndex = (currentImageIndex + 1) % currentPostImages.length;
                setCurrentImageIndex(newIndex);
                setLightboxImage(currentPostImages[newIndex]);
              }}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <polyline points="9 18 15 12 9 6" strokeWidth="2"/>
                </svg>
              </button>
            </>
          )}

          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <img src={resolveMediaUrl(lightboxImage.image)} alt={lightboxImage.caption || ""} />
            {lightboxImage.caption && (
              <p className="lightbox-caption">{lightboxImage.caption}</p>
            )}
            {currentPostImages.length > 1 && (
              <p className="lightbox-counter">
                {currentImageIndex + 1} / {currentPostImages.length}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Blog;